import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useTypedSelector } from "hooks";
import { useSimpleRequest } from "hooks/useRequest";
import { ApiState } from "infrastructure/api";
import { Paginate } from "consts/table";
import { RequestQueryMapper } from "util/request-query-mapper";
import {
    selectIsPaymentMethodsLoading,
    selectPaymentMethodsData,
    selectPaymentMethodsPaginator
} from "features/paymentMethods/selectors";
import { getPaymentMethods as getPaymentMethodsRequest } from "features/paymentMethods/api";
import {
    changePaymentMethodsLoadingState,
    changePaymentMethodsSlice
} from "features/paymentMethods/slice";
import type { PaymentMethod } from "features/paymentMethods/types";
import { stripForDuplicate } from "../PaymentMethod/PaymentMethodProvider";

const DEFAULT_PAGE_SIZE = 10;

export default function usePaymentMethods() {
    const dispatch = useAppDispatch();
    const request = useSimpleRequest();
    const isPaymentMethodsLoading = useTypedSelector(selectIsPaymentMethodsLoading);
    const paymentMethods = useTypedSelector(selectPaymentMethodsData());
    const paginator = useTypedSelector(selectPaymentMethodsPaginator);

    const [duplicateSeed, setDuplicateSeed] = useState<PaymentMethod | null>(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);
    const [searchCriteria, setSearchCriteriaState] = useState('');
    const [providers, setProvidersState] = useState<string[]>([]);
    const [tags, setTagsState] = useState<string[]>([]);
    const [providerOptions, setProviderOptions] = useState<string[]>([]);
    const [tagOptions, setTagOptions] = useState<string[]>([]);

    useEffect(() => {
        let isActive = true;
        const distinctQuery = (field: 'PSP' | 'tag') => RequestQueryMapper.from()
            .distincts(field)
            .contains(Paginate.perPage, '1000')
            .toString();
        const getResources = (response: any): PaymentMethod[] => response?.data?.data ?? [];

        Promise.all([
            request(() => getPaymentMethodsRequest(distinctQuery('PSP'))),
            request(() => getPaymentMethodsRequest(distinctQuery('tag')))
        ]).then(([providerResponse, tagResponse]) => {
            if (!isActive) return;

            setProviderOptions(Array.from(new Set(
                getResources(providerResponse).map(paymentMethod => paymentMethod.PSP).filter(Boolean)
            )).sort());
            setTagOptions(Array.from(new Set(
                getResources(tagResponse).map(paymentMethod => paymentMethod.tag).filter(Boolean)
            )).sort());
        });

        return () => { isActive = false; };
    }, [request]);

    useEffect(() => {
        let isActive = true;
        const query = RequestQueryMapper.from()
            .contains(Paginate.page, String(page))
            .contains(Paginate.perPage, String(perPage));

        if (searchCriteria.trim()) {
            query
                .checkWithModifier('name', 'contains')
                .values('name', searchCriteria.trim());
        }
        providers.forEach(provider => query.containsIn('PSP', provider));
        tags.forEach(tag => query.containsIn('tag', tag));

        dispatch(changePaymentMethodsLoadingState(ApiState.Pending));
        request(() => getPaymentMethodsRequest(query.toString()), {
            onSuccess: response => {
                if (!isActive) return;
                dispatch(changePaymentMethodsSlice(response));
                dispatch(changePaymentMethodsLoadingState(ApiState.Succeeded));
            },
            onError: () => {
                if (isActive) dispatch(changePaymentMethodsLoadingState(ApiState.Failed));
            }
        }).catch(() => {
            if (isActive) dispatch(changePaymentMethodsLoadingState(ApiState.Failed));
        });

        return () => { isActive = false; };
    }, [dispatch, page, perPage, providers, request, searchCriteria, tags]);

    // This screen stores its paginated/filtered result in the shared payment-methods
    // slice. Mark it stale on exit so consumers that require the full collection
    // (merchant filters and assignment dialogs) fetch it again.
    useEffect(() => () => {
        dispatch(changePaymentMethodsLoadingState(ApiState.Idle));
    }, [dispatch]);

    // This screen stores its paginated/filtered result in the shared payment-methods
    // slice. Mark it stale on exit so consumers that require the full collection
    // (merchant filters and assignment dialogs) fetch it again.
    useEffect(() => () => {
        dispatch(changePaymentMethodsLoadingState(ApiState.Idle));
    }, [dispatch]);

    const getPaymentMethods = () => paymentMethods;

    const resetPage = <T,>(setter: (value: T) => void) => (value: T) => {
        setter(value);
        setPage(1);
    };

    const setSearchCriteria = resetPage(setSearchCriteriaState);
    const setProviders = resetPage(setProvidersState);
    const setTags = resetPage(setTagsState);

    const changePageSize = (value: number) => {
        setPerPage(value);
        setPage(1);
    };

    const requestDuplicate = useCallback((paymentMethod: PaymentMethod) => {
        setDuplicateSeed(stripForDuplicate(paymentMethod));
    }, []);

    const dismissDuplicate = useCallback(() => {
        setDuplicateSeed(null);
    }, []);

    return {
        getPaymentMethods,
        isPaymentMethodsLoading,
        paginator,
        page,
        perPage,
        providers,
        tags,
        searchCriteria,
        providerOptions,
        tagOptions,
        setPage,
        setPerPage: changePageSize,
        setProviders,
        setTags,
        setSearchCriteria,
        duplicateSeed,
        requestDuplicate,
        dismissDuplicate
    };
};
